import { useCallback, useContext, useRef } from 'react';
import { useGridMode } from 'contexts/GridModeContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { useAppDispatch, useAppSelector } from 'hooks';
import { updatePlayerGames } from 'reducers/playerGames';
import { removeTable } from 'reducers/tables';
import { pauseTableHandHistorySessions } from 'utils/handHistorySession';
import { isStillOffLobby, isStillOnTable, retryPastTransition } from 'utils/navigationRetry';

import { useActiveTables } from 'components/SaloonLobby/useActiveTables';

export { EXIT_NAVIGATION_ATTEMPTS, EXIT_NAVIGATION_RETRY_MS } from 'utils/navigationRetry';

/**
 * Shared "close this table and move on" navigation used by both the top-right
 * Exit Table button and the idle Buy Back In modal's Exit Table.
 *
 * Removes the table from BOTH client-side sources that "Your Tables" is built
 * from — `state.tables` and `state.playerGames` — and then:
 *   • grid view  → nothing else; the grid re-renders with the remaining cells.
 *   • single view → hops to the NEAREST still-open table — the one to the LEFT
 *     of the closed table in the navigation strip's add-order, or the new
 *     left-most table when the closed one was already left-most — and returns
 *     to the lobby only when this was the last open table.
 *
 * Dropping only the `tables` slice is what made an exited table come BACK a
 * while later: useActiveTables rebuilds the list from playerGames as well (its
 * Pass 2 covers tables the engine reports before their first broadcast lands),
 * and the exited table sat in that stale list untouched. The recently-exited
 * mark hid it for 30 seconds; the moment that lapsed the stale entry surfaced
 * again — and the auto-resubscribe then made it real once more. The SnG
 * unregister path in App.tsx already filtered playerGames for exactly this
 * reason; the ordinary Exit Table path did not.
 *
 * Callers own the seat-side teardown (leaveTable / waitlist exit) before calling
 * this — it handles ONLY the table removal + view navigation so the two exit
 * paths can't drift apart.
 *
 * `opts.navigate` overrides the grid check, for a caller outside any table
 * cell (where useGridMode is always false): it closes a table the player may
 * not be looking at, and should only move the view when they are.
 *
 * `opts.alsoLeaving`: other tables the caller is exiting in the same pass. The
 * view never moves onto one of them — it would be removed a moment later,
 * leaving the player on a dead table's URL.
 *
 * `opts.onReturnToLobby`: runs just before the view falls back to the lobby,
 * and only then — never when the exit hops to another open table. A finished
 * Sit & Go uses it to arm the lobby's Sit & Go tab (EN-11109).
 *
 * `opts.toLobby`: land on the lobby even when other tables are open, for an
 * exit the player asked to take them to the lobby (a finished Sit & Go's Go to
 * lobby).
 */
export function useExitToNextTable() {
  const dispatch = useAppDispatch();
  const isGridMode = useGridMode();
  const { returnToLobby } = useContext(ViewManagerContext);
  const { activeTables, openTable } = useActiveTables();
  const playerGames = useAppSelector((state) => (state as any).playerGames) as any[];
  // The list each exit filters. Updated by the exit itself, so a second exit in
  // the same render (the Announced-table watcher closes them in one pass)
  // filters what the first left — not the render's list, which would put the
  // first table back.
  const playerGamesRef = useRef(playerGames);
  playerGamesRef.current = playerGames;

  return useCallback(
    (
      gameID: string | number,
      opts?: {
        navigate?: boolean;
        alsoLeaving?: (string | number)[];
        onReturnToLobby?: () => void;
        toLobby?: boolean;
      }
    ) => {
      pauseTableHandHistorySessions(String(gameID));
      dispatch(removeTable(String(gameID)));
      // …and out of the engine-reported list, so the table can't be rebuilt
      // from it once the recently-exited mark lapses. A fresh GOT_PLAYER_GAMES
      // overwrites this wholesale, so nothing is lost if the engine still
      // reports the table — the auto-resubscribe skip and the exit mark cover
      // that window.
      const current = playerGamesRef.current || [];
      const remaining = current.filter((g: any) => g && String(g.gameID) !== String(gameID));
      if (remaining.length !== current.length) {
        playerGamesRef.current = remaining;
        dispatch(updatePlayerGames(remaining));
      }
      if (opts?.navigate ?? !isGridMode) {
        // Closed index in the strip's add-order; its nearest LEFT neighbour
        // that stays becomes the active table so the player lands beside where
        // they were, matching the highlight the strip moves to. None to the
        // left → the new left-most table. An unknown gameID (already gone)
        // falls back to the left-most remaining table. Tables leaving in the
        // same pass are not candidates.
        const leaving = new Set([gameID, ...(opts?.alsoLeaving || [])].map(String));
        const stays = (t: { gameID: string }) => !leaving.has(String(t.gameID));
        const closedIdx = activeTables.findIndex((t) => String(t.gameID) === String(gameID));
        const leftOfClosed = activeTables.slice(0, Math.max(0, closedIdx)).filter(stays);
        const next = opts?.toLobby
          ? undefined
          : leftOfClosed[leftOfClosed.length - 1] ?? activeTables.filter(stays)[0];
        const moveOff = () => {
          if (next) {
            // forceSingle — a table switch must never re-route the view (same
            // rule as the dock/strip taps in TableHeader and the swipe
            // gestures). The exit happened INSIDE single-table view, so the
            // hop has to land there too. Without it, ViewManager's sticky
            // multi-table preference — which the add-a-table auto-grow flips
            // ON as soon as a second table lands, e.g. observing a tournament
            // table from the one already open — sent this already-open table
            // to /grid, and Exit Table threw the player into a grid they never
            // asked for (EN-10001 follow-up).
            openTable(next.gameID, next.game, 'no', { forceSingle: true });
          } else {
            opts?.onReturnToLobby?.();
            returnToLobby();
          }
        };
        // An exit the player asked to take them to the lobby is retried from
        // the grid too; any other exit only while the view is on this table.
        const id = String(gameID);
        retryPastTransition(
          () => (opts?.toLobby ? isStillOffLobby(id) : isStillOnTable(id)),
          moveOff
        );
      }
    },
    [dispatch, isGridMode, activeTables, openTable, returnToLobby]
  );
}
